
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { TeacherMonthlyReport } from "@/types/salary";
import { CommissionDetails } from './CommissionDetails';

interface SalaryConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  monthlyReport: TeacherMonthlyReport;
  onConfirm: () => void;
}

export function SalaryConfirmationDialog({
  isOpen,
  onClose,
  monthlyReport,
  onConfirm,
}: SalaryConfirmationDialogProps) {
  const { toast } = useToast();

  const handleConfirm = async () => {
    try {
      const { error } = await supabase
        .from('teacher_monthly_reports')
        .update({
          confirmation_status: 'ACCEPTED',
          confirmed_at: new Date().toISOString()
        })
        .eq('teacher_id', monthlyReport.teacher_id)
        .eq('month', monthlyReport.month)
        .eq('year', monthlyReport.year);

      if (error) throw error;

      toast({
        title: "ยืนยันเงินเดือนสำเร็จ",
        description: "คุณได้ยืนยันว่าจำนวนเงินเดือนถูกต้อง"
      });
      
      onConfirm();
      onClose();
    } catch (error) {
      toast({
        title: "เกิดข้อผิดพลาด",
        description: "ไม่สามารถยืนยันเงินเดือนได้ กรุณาลองใหม่อีกครั้ง",
        variant: "destructive"
      });
    }
  };

  const commissionAmount = typeof monthlyReport.commission_amount === 'number' ? monthlyReport.commission_amount : 0;
  const totalSalary = typeof monthlyReport.total_salary === 'number' ? monthlyReport.total_salary : 0;
  const otherAmount = typeof monthlyReport.other_amount === 'number' ? monthlyReport.other_amount : 0;
  const deductionAmount = typeof monthlyReport.deduction_amount === 'number' ? monthlyReport.deduction_amount : 0;
  const depositRefund = typeof monthlyReport.deposit_refund === 'number' ? monthlyReport.deposit_refund : 0;
  const depositDeduction = typeof monthlyReport.deposit_deduction === 'number' ? monthlyReport.deposit_deduction : 0;

  const subTotal = totalSalary + commissionAmount;
  const totalAmount = subTotal + otherAmount - deductionAmount;
  const afterTaxTotal = +(totalAmount * 0.97).toFixed(2); // Calculate after 3% tax with 2 decimal places
  const finalTotalWithDeposit = afterTaxTotal + depositRefund - depositDeduction;

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>ยืนยันเงินเดือน</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>กรุณายืนยันว่าจำนวนเงินเดือนทั้งหมดถูกต้อง</p>
            <div className="font-medium space-y-1">
              <p>รายได้จากการสอน: {totalSalary.toLocaleString()} บาท</p>
              <div>
                <p>ค่าคอมมิชชั่น: {commissionAmount.toLocaleString()} บาท</p>
                <CommissionDetails details={monthlyReport.commission_details} />
              </div>
              {deductionAmount > 0 && (
                <p className="text-red-500">หัก: {deductionAmount.toLocaleString()} บาท
                  {monthlyReport.deduction_reason && (
                    <span className="text-sm ml-2">({monthlyReport.deduction_reason})</span>
                  )}
                </p>
              )}
              {otherAmount > 0 && (
                <p className="text-blue-500">อื่นๆ: {otherAmount.toLocaleString()} บาท
                  {monthlyReport.other_reason && (
                    <span className="text-sm ml-2">({monthlyReport.other_reason})</span>
                  )}
                </p>
              )}
              <p className="text-lg mt-2">รวมทั้งหมด: {totalAmount.toLocaleString()} บาท</p>
              <p className="text-lg text-blue-600">รายได้หลังหักภาษี 3%: {afterTaxTotal.toLocaleString()} บาท</p>
              
              <div className="border-t pt-3 mt-3">
                <p className="font-semibold">ข้อมูลค่าประกัน:</p>
                {depositRefund > 0 && (
                  <p className="text-green-500">คืนค่าประกัน: {depositRefund.toLocaleString()} บาท</p>
                )}
                {depositDeduction > 0 && (
                  <p className="text-red-500">หักค่าประกัน: {depositDeduction.toLocaleString()} บาท</p>
                )}
                {(depositRefund > 0 || depositDeduction > 0) && (
                  <p className="text-lg font-bold text-purple-600 mt-2">
                    ยอดเงินทั้งหมดรวมค่าประกัน: {finalTotalWithDeposit.toLocaleString()} บาท
                  </p>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onClose}>ยกเลิก</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirm}>ยืนยัน</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
